import { Ring } from '@blocks/Loading'
import { useConfigContext } from '@contexts/config'
import { useSliderContext } from '@contexts/slider'
import usePhotoController from '@customer/api/usePhotoController'
import validate from '@customer/models/Photo/helpers/validate'
import { confirmation, notify } from '@helpers'
import useLoading from '@hooks/useLoading'
import { ReactComponent as DeleteIcon } from '@icons/delete.svg'
import { ReactComponent as ImageIcon } from '@icons/image.svg'
import { colors } from '@styles/vars'
import React from 'react'
import styled from 'styled-components'

export default function PhotoActions({ photo, onChange, onDelete }: Props) {
    const config = useConfigContext()
    const slider = useSliderContext()
    const controller = usePhotoController()
    const loading = useLoading()

    const handleImageChange: React.InputHTMLAttributes<HTMLInputElement>["onChange"] = async (e) => {
        if (!e.target.files?.length) return

        const image = e.target.files[0]

        const validated = validate.image(image, config)
        if (!validated) return

        loading.until(async () => {
            const response = await controller.update(photo.id, { image })
            onChange(response)
        })
    }

    const handleDelete = async () => {
        if (!onDelete) return

        const confirmed = await confirmation("Are you sure you want to delete the photo?")
        if (!confirmed) return

        slider.close()
        await controller.destroy(photo.id)
        onDelete(photo.id)
        notify(`'${photo.title}' was deleted.`)
    }

    if (!photo.deletable)
        return <Notice>You can't delete the photo or change the image because it's already participated in salons. The title can be changed at any time.</Notice>
    
    return (
        <Actions>
            <ActionFile>
                <input id={`photo-${photo.id}`} type="file" accept="image/jpeg" onChange={handleImageChange} />
                <label htmlFor={`photo-${photo.id}`}>
                    {loading.state 
                        ? <Ring fill={colors.white} height={18} /> 
                        : <ImageIcon />
                    } Replace</label>
            </ActionFile>
            <Action onClick={handleDelete}><DeleteIcon />Delete</Action>
        </Actions>
    )
}

const Notice = styled.small`
    max-width: 430px;
`

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;

    & > *:not(:last-child) {
        margin-right: 7px;
    }
`

const Action = styled.small`
    display: flex;
    align-items: center;
    font-weight: 600;
    cursor: pointer;
    padding: 3px 5px;
    border-radius: 4px;
    transition: .2s ease-out;
    opacity: .8;
    z-index: 1;
    
    svg {
        margin-right: 7px;
        height: 16px;
        width: 16px;
    }

    &:hover {
        background: hsla(0, 0%, 100%, .1);
        opacity: 1;
    }


    & > div {
        margin-right: 3px;
    }
`

const ActionFile = styled(Action)`
    input {
        position: absolute;
        opacity: 0;
        z-index: -1;
    }

    label {
        display: flex;
        align-items: center;
        cursor: pointer;


        & > div {
            margin-right: 3px;
        }
    }
    
    svg {
        fill: ${colors.white} !important;
    }
`

interface Props {
    photo: Interfaces.Photo
    onChange(photo: Interfaces.Photo): any
    onDelete?(photoId: number): any
}